import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Dumbbell, 
  Apple, 
  Moon, 
  Calendar,
  Flame,
  Droplets,
  TrendingUp,
  ChevronRight,
  Sparkles,
  Zap,
  Trophy,
  Building2,
  BookOpen,
  Share2,
  Quote,
  Target,
  GraduationCap,
  Star,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { getDailyQuote } from '../data/motivationalData';
import { toast } from 'sonner';
import DailyQuote from '../components/DailyQuote';
import DailyChallenges from '../components/DailyChallenges';

const AthleteDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [auraScore, setAuraScore] = useState(0);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performanceMessage, setPerformanceMessage] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
    fetchAuraScore();
    fetchProfile();
    fetchPerformanceMessage();
  }, []);

  const fetchPerformanceMessage = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/performance/message`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerformanceMessage(response.data);
    } catch (error) {
      console.error('Failed to fetch performance message:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      // Send user's local date to ensure proper daily reset at midnight local time
      const localDate = new Date().toLocaleDateString('en-CA'); // Format: YYYY-MM-DD
      const response = await axios.get(`${API}/dashboard/stats?local_date=${localDate}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuraScore = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/badges`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAuraScore(response.data.total_points || 0);
    } catch (error) {
      console.error('Failed to fetch aura score:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/athlete/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      // Profile not created yet
    }
  };

  const getRecoveryColor = (score) => {
    if (score >= 80) return 'text-aura-success';
    if (score >= 60) return 'text-aura-secondary';
    if (score >= 40) return 'text-aura-warning';
    return 'text-aura-destructive';
  };

  const getRecoveryLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Moderate';
    return 'Rest Day';
  };

  const quickActions = [
    { icon: Dumbbell, label: 'Execute Session', path: '/workouts?action=new', color: 'bg-aura-primary/20 text-aura-primary' },
    { icon: Apple, label: 'Fuel Up', path: '/nutrition?action=new', color: 'bg-aura-success/20 text-aura-success' },
    { icon: Target, label: 'My Goals', path: '/goals', color: 'bg-purple-500/20 text-purple-400' },
    { icon: Share2, label: 'Share Aura', path: '/share', color: 'bg-yellow-500/20 text-yellow-400' },
  ];

  const pillars = [
    { 
      icon: Dumbbell, 
      name: 'Training', 
      value: stats?.workouts_this_week || 0, 
      target: 5, 
      unit: 'workouts',
      color: 'aura-primary',
      path: '/workouts'
    },
    { 
      icon: Apple, 
      name: 'Nutrition', 
      value: stats?.today_calories || 0, 
      target: 2500, 
      unit: 'cal',
      color: 'aura-success',
      path: '/nutrition'
    },
    { 
      icon: Droplets, 
      name: 'Hydration', 
      value: 64, // TODO: fetch from tracker 
      target: 100, 
      unit: 'oz',
      color: 'aura-secondary',
      path: '/recovery'
    },
    { 
      icon: Moon, 
      name: 'Recovery', 
      value: stats?.recovery_score || 0, 
      target: 100, 
      unit: 'score',
      color: 'aura-warning',
      path: '/recovery'
    },
  ];

  return (
    <DashboardLayout>
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
              Your Development System
            </h1>
            <p className="text-aura-text-muted mt-1">
              Execute your plan. Build your performance.
            </p>
          </div>
          
          {/* Aura Score Badge - Links to Badges Page */}
          <Link to="/badges" className="aura-card p-4 flex items-center gap-4 hover:border-white/20 transition-all">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">Aura Score</p>
              <p className="font-heading font-bold text-2xl text-white">{auraScore}</p>
            </div>
          </Link>
        </div>

        {/* Quick Actions - Mobile Optimized */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map((action) => (
            <Link
              key={action.label}
              to={action.path}
              className="aura-card p-4 flex flex-col items-center justify-center gap-2 hover:border-white/20 transition-all active:scale-95"
              data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center`}>
                <action.icon className="w-6 h-6" />
              </div>
              <span className="text-sm font-medium text-white text-center">{action.label}</span>
            </Link>
          ))}
        </div>

        {/* Motivation Section - Why Statement & Goals */}
        {(profile?.whyStatement || profile?.dreamSchools?.length > 0 || profile?.shortTermGoals?.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-4">
            {/* My Why Card */}
            {profile?.whyStatement && (
              <Link to="/my-profile" className="block">
                <Card className="bg-gradient-to-br from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30 hover:border-aura-primary/50 transition-all h-full">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-aura-primary rounded-lg flex items-center justify-center flex-shrink-0">
                        <Quote className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-1">Remember Your Why</p>
                        <p className="text-white italic line-clamp-2">"{profile.whyStatement}"</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}

            {/* Dream Schools Quick View */}
            {profile?.dreamSchools?.length > 0 && (
              <Link to="/my-profile" className="block">
                <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/30 hover:border-purple-500/50 transition-all h-full">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-2">Dream Schools</p>
                        <div className="flex flex-wrap gap-1">
                          {profile.dreamSchools.slice(0, 3).map((schoolId, idx) => (
                            <Badge key={schoolId} className="bg-purple-500/20 text-purple-300 border-0 text-xs">
                              #{idx + 1}
                            </Badge>
                          ))}
                          {profile.dreamSchools.length > 3 && (
                            <Badge className="bg-white/10 text-aura-text-muted border-0 text-xs">
                              +{profile.dreamSchools.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        )}

        {/* Goals Progress */}
        {(profile?.shortTermGoals?.length > 0 || profile?.longTermGoals?.length > 0) && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                  <Target className="w-5 h-5 text-yellow-400" />
                  Goal Progress
                </CardTitle>
                <Link to="/my-profile" className="text-aura-secondary text-sm hover:text-aura-secondary/80">
                  View All <ChevronRight className="w-4 h-4 inline" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Short Term */}
                {profile?.shortTermGoals?.length > 0 && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-aura-text-muted">Short-Term</span>
                      <span className="text-sm font-medium text-yellow-400">
                        {profile.shortTermGoals.filter(g => g.completed).length}/{profile.shortTermGoals.length}
                      </span>
                    </div>
                    <Progress 
                      value={(profile.shortTermGoals.filter(g => g.completed).length / profile.shortTermGoals.length) * 100} 
                      className="h-2 mb-2" 
                    />
                    {profile.shortTermGoals.filter(g => !g.completed).slice(0, 2).map((goal, idx) => (
                      <div key={idx} className="flex items-center gap-2 mt-2">
                        <div className="w-4 h-4 rounded-full border border-white/30" />
                        <span className="text-sm text-white truncate">{goal.title}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Long Term */}
                {profile?.longTermGoals?.length > 0 && (
                  <div className="p-3 bg-white/5 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-aura-text-muted">Long-Term</span>
                      <span className="text-sm font-medium text-aura-primary">
                        {profile.longTermGoals.filter(g => g.completed).length}/{profile.longTermGoals.length}
                      </span>
                    </div>
                    <Progress 
                      value={(profile.longTermGoals.filter(g => g.completed).length / profile.longTermGoals.length) * 100} 
                      className="h-2 mb-2" 
                    />
                    {profile.longTermGoals.filter(g => !g.completed).slice(0, 2).map((goal, idx) => (
                      <div key={idx} className="flex items-center gap-2 mt-2">
                        <div className="w-4 h-4 rounded-full border border-white/30" />
                        <span className="text-sm text-white truncate">{goal.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Setup Profile CTA - Show if no profile data */}
        {!profile?.whyStatement && !profile?.dreamSchools?.length && (
          <Card className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/30 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="font-heading font-bold text-white text-sm uppercase tracking-tight">Set Your Goals</p>
                  <p className="text-xs text-aura-text-muted">Add your "Why" and dream schools to stay motivated</p>
                </div>
              </div>
              <Link to="/my-profile">
                <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white">
                  Setup Profile
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Daily Quote & Challenges Grid */}
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Daily Quote */}
          <DailyQuote showRefresh={true} />
          
          {/* Daily Challenges */}
          <DailyChallenges />
        </div>

        {/* Join Team CTA - Show only if not in an organization */}
        {!user?.org_id && (
          <Card className="bg-gradient-to-r from-aura-secondary/20 to-aura-primary/20 border-aura-secondary/30">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-aura-secondary/30 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-aura-secondary" />
                </div>
                <div>
                  <p className="font-heading font-bold text-white text-sm uppercase tracking-tight">Join Your Team</p>
                  <p className="text-xs text-aura-text-muted">Have an invite code from your coach?</p>
                </div>
              </div>
              <Link to="/organization/join">
                <Button size="sm" className="bg-aura-secondary hover:bg-aura-secondary/90 text-white" data-testid="join-team-btn">
                  Join
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Recovery Status - Featured Card */}
        {stats?.recovery_score !== undefined && (
          <Card className="bg-aura-surface border-white/10 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-1">Today's Recovery</p>
                  <div className="flex items-baseline gap-2">
                    <span className={`font-heading font-bold text-4xl ${getRecoveryColor(stats.recovery_score)}`}>
                      {stats.recovery_score}
                    </span>
                    <span className="text-aura-text-muted">/100</span>
                  </div>
                  <p className={`text-sm font-medium ${getRecoveryColor(stats.recovery_score)}`}>
                    {getRecoveryLabel(stats.recovery_score)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-1">Sleep</p>
                  <p className="font-heading font-bold text-2xl text-white">
                    {stats.sleep_hours || '--'}h
                  </p>
                </div>
              </div>
              <Progress 
                value={stats.recovery_score} 
                className="h-2 bg-white/10"
              />
            </CardContent>
          </Card>
        )}

        {/* Pillars Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {pillars.map((pillar) => (
            <Link 
              key={pillar.name}
              to={pillar.path}
              className="aura-card p-4 hover:border-white/20 transition-all"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg bg-${pillar.color}/20 flex items-center justify-center`}>
                  <pillar.icon className={`w-5 h-5 text-${pillar.color}`} />
                </div>
                <span className="font-heading font-bold text-sm text-white uppercase tracking-wider">
                  {pillar.name}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading font-bold text-2xl text-white">
                    {pillar.value}
                  </span>
                  <span className="text-aura-text-muted text-sm">
                    / {pillar.target} {pillar.unit}
                  </span>
                </div>
                <Progress 
                  value={(pillar.value / pillar.target) * 100} 
                  className="h-1.5 bg-white/10"
                />
              </div>
            </Link>
          ))}
        </div>

        {/* Upcoming Events */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider">
                Upcoming
              </CardTitle>
              <Link to="/schedule" className="text-aura-primary text-sm font-medium flex items-center gap-1">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats?.upcoming_events && stats.upcoming_events.length > 0 ? (
              stats.upcoming_events.slice(0, 3).map((event, index) => (
                <div 
                  key={event.event_id || index}
                  className="flex items-center gap-4 p-3 rounded-lg bg-white/5"
                >
                  <div className="w-10 h-10 rounded-lg bg-aura-primary/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-aura-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{event.title}</p>
                    <p className="text-sm text-aura-text-muted">
                      {new Date(event.start_time).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6">
                <Calendar className="w-10 h-10 text-aura-text-subtle mx-auto mb-2" />
                <p className="text-aura-text-muted">Your system is ready. Let's get to work.</p>
                <Link to="/schedule?action=new">
                  <Button variant="link" className="text-aura-primary mt-2">
                    Add Event
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance Message & Daily Quote */}
        <div className="space-y-4">
          {/* Contextual Performance Message */}
          {performanceMessage && (
            <div className={`aura-card p-6 border-l-4 ${
              performanceMessage.type === 'success' ? 'border-l-green-500 bg-green-500/5' :
              performanceMessage.type === 'milestone' ? 'border-l-yellow-500 bg-yellow-500/5' :
              performanceMessage.type === 'warning' ? 'border-l-amber-500 bg-amber-500/5' :
              performanceMessage.type === 'alert' ? 'border-l-red-500 bg-red-500/5' :
              performanceMessage.type === 'comeback' ? 'border-l-purple-500 bg-purple-500/5' :
              'border-l-aura-primary bg-aura-primary/5'
            }`} data-testid="performance-message">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  performanceMessage.type === 'success' ? 'bg-green-500/20' :
                  performanceMessage.type === 'milestone' ? 'bg-yellow-500/20' :
                  performanceMessage.type === 'warning' ? 'bg-amber-500/20' :
                  performanceMessage.type === 'alert' ? 'bg-red-500/20' :
                  performanceMessage.type === 'comeback' ? 'bg-purple-500/20' :
                  'bg-aura-primary/20'
                }`}>
                  {performanceMessage.type === 'success' && <Flame className="w-6 h-6 text-green-400" />}
                  {performanceMessage.type === 'milestone' && <Trophy className="w-6 h-6 text-yellow-400" />}
                  {performanceMessage.type === 'warning' && <Zap className="w-6 h-6 text-amber-400" />}
                  {performanceMessage.type === 'alert' && <Target className="w-6 h-6 text-red-400" />}
                  {performanceMessage.type === 'comeback' && <Star className="w-6 h-6 text-purple-400" />}
                  {performanceMessage.type === 'info' && <Sparkles className="w-6 h-6 text-aura-primary" />}
                </div>
                <div className="flex-1">
                  <h3 className={`font-heading font-bold text-lg uppercase tracking-tight ${
                    performanceMessage.type === 'success' ? 'text-green-400' :
                    performanceMessage.type === 'milestone' ? 'text-yellow-400' :
                    performanceMessage.type === 'warning' ? 'text-amber-400' :
                    performanceMessage.type === 'alert' ? 'text-red-400' :
                    performanceMessage.type === 'comeback' ? 'text-purple-400' :
                    'text-aura-primary'
                  }`}>
                    {performanceMessage.title}
                  </h3>
                  <p className="text-white mt-1">{performanceMessage.message}</p>
                  {performanceMessage.stats && (
                    <div className="flex items-center gap-4 mt-3 text-sm">
                      {performanceMessage.stats.current_streak > 0 && (
                        <span className="flex items-center gap-1 text-aura-text-muted">
                          <Flame className="w-4 h-4 text-orange-400" />
                          {performanceMessage.stats.current_streak} day streak
                        </span>
                      )}
                      {performanceMessage.stats.workouts_this_week > 0 && (
                        <span className="flex items-center gap-1 text-aura-text-muted">
                          <Dumbbell className="w-4 h-4 text-aura-primary" />
                          {performanceMessage.stats.workouts_this_week} this week
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Daily Motivation Quote */}
          <div className="aura-card p-6 text-center bg-gradient-to-br from-aura-primary/5 to-transparent">
            <Quote className="w-8 h-8 text-aura-primary mx-auto mb-3" />
            <p className="font-heading font-bold text-lg text-white italic">
              "{getDailyQuote().quote}"
            </p>
            <p className="text-aura-primary mt-3 text-sm font-medium">
              — {getDailyQuote().author}
            </p>
            <p className="text-aura-text-muted text-xs mt-1">
              {getDailyQuote().sport}
            </p>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-aura-text-muted uppercase tracking-wider">
                Aura is earned, not given.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AthleteDashboard;
